import { createStore, get, set } from 'idb-keyval';
import { distanceM } from '../lib/geo';
import { fetchProfile, profilePoints } from '../lib/elevation';
import { lineOfSight, DEFAULT_FREQ_GHZ, DEFAULT_SITE_HEIGHT_M, type LosResult } from '../lib/los';
import type { Site } from '../lib/cluster';
import { getState, setState, homeHeight } from './state';

/** Terrain profiles are stored on the device: they never change and are needed offline on the roof. */
const store = createStore('antenne-profiles', 'profiles');
const profiles = new Map<string, number[]>();
const inflight = new Map<string, Promise<LosResult | null>>();
const GAP_MS = 1500; // keep well under the free elevation API quota
let lastFetch = 0;
let pausedUntil = 0;

const keyFor = (site: Site) => {
  const home = getState().settings.home!;
  return `${site.lat.toFixed(5)},${site.lon.toFixed(5)}@${home.lat.toFixed(5)},${home.lon.toFixed(5)}`;
};

async function loadProfile(site: Site): Promise<number[] | null> {
  const key = keyFor(site);
  const mem = profiles.get(key);
  if (mem) return mem;
  const saved = await get<number[]>(key, store).catch(() => undefined);
  if (saved) {
    profiles.set(key, saved);
    return saved;
  }
  if (Date.now() < pausedUntil) return null;
  const wait = lastFetch + GAP_MS - Date.now();
  if (wait > 0) await new Promise(r => setTimeout(r, wait));
  lastFetch = Date.now();
  const home = getState().settings.home!;
  const ground = await fetchProfile(home, site, profilePoints(distanceM(home, site)));
  if (!ground) {
    pausedUntil = Date.now() + 60_000; // likely rate limited or offline: back off for a minute
    return null;
  }
  profiles.set(key, ground);
  void set(key, ground, store).catch(() => undefined);
  return ground;
}

/** Line of sight for one site from the current home. null when elevations are unavailable. */
export function losFor(site: Site): Promise<LosResult | null> {
  if (!getState().settings.home) return Promise.resolve(null);
  const cached = getState().los.get(site.id);
  if (cached) return Promise.resolve(cached);
  const key = keyFor(site);
  if (inflight.has(key)) return inflight.get(key)!;
  const p = (async () => {
    const ground = await loadProfile(site);
    const home = getState().settings.home;
    if (!ground || !home) return null;
    const r = lineOfSight({ distanceM: distanceM(home, site), ground, hHomeM: homeHeight(), hSiteM: DEFAULT_SITE_HEIGHT_M, freqGHz: DEFAULT_FREQ_GHZ });
    const los = new Map(getState().los);
    los.set(site.id, r);
    setState({ los });
    return r;
  })().finally(() => inflight.delete(key));
  inflight.set(key, p);
  return p;
}

export function profileFor(site: Site): number[] | undefined {
  if (!getState().settings.home) return undefined;
  return profiles.get(keyFor(site));
}

let warming = false;

/** Compute line of sight for the nearest `perNetwork` sites of each network, nearest first, one request at a time. */
export async function warmRanking(perNetwork = 4): Promise<void> {
  if (warming) return;
  warming = true;
  try {
    const { settings, sites } = getState();
    const home = settings.home;
    if (!home) return;
    const by = new Map<string, Site[]>();
    for (const s of sites) {
      if (!by.has(s.network)) by.set(s.network, []);
      by.get(s.network)!.push(s);
    }
    const todo: Site[] = [];
    for (const list of by.values()) {
      list.sort((a, b) => distanceM(home, a) - distanceM(home, b));
      todo.push(...list.slice(0, perNetwork));
    }
    todo.sort((a, b) => distanceM(home, a) - distanceM(home, b));
    for (const s of todo) {
      if (getState().settings.home !== home) {
        setTimeout(() => void warmRanking(perNetwork), 0); // home moved: restart for the new position
        return;
      }
      const r = await losFor(s);
      if (!r && Date.now() < pausedUntil) {
        setTimeout(() => void warmRanking(perNetwork), pausedUntil - Date.now() + 500);
        return;
      }
    }
  } finally {
    warming = false;
  }
}
